import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../api-wa/client';
import { useConfig } from '../../Context/WaConfigContext';
import {
  createCampaign,
  deleteCampaign,
  getCampaign,
  getCampaignMessages,
  getCampaignHistory,
  getCampaigns,
  getScheduledCampaigns,
  launchCampaign,
  scheduleCampaign,
  stopCampaign,
} from '../../api-wa/campaignsApi';
import { getContacts } from '../../api-wa/contactsApi';
import { getWhatsAppTemplates } from '../../api-wa/templatesApi';
import { getCourses } from '../../api-wa/coursesApi';
import { DeleteConfirmModal } from '../../Components/WhatsApp/ConfirmationModals';
import '../../styles-wa/campaigns_view.css';

const PAGE_COPY = {
  active: {
    title: 'Campaign Manager',
    subtitle: '',
    listTitle: 'Active Campaigns',
    empty: 'No active campaigns yet. Create one to get started.',
  },
  history: {
    title: 'Campaign History',
    subtitle: '',
    listTitle: 'Campaign History',
    empty: 'No completed or failed campaigns found.',
  },
  scheduled: {
    title: 'Scheduled Campaigns',
    subtitle: '',
    listTitle: 'Scheduled Campaigns',
    empty: 'No scheduled campaigns found.',
  },
};

const toList = (data) => data?.results ?? data ?? [];

const extractTemplateVariables = (template) => {
  const body = template?.body || template?.body_preview || '';
  const matches = [...body.matchAll(/\{\{(\d+)\}\}/g)].map((match) => match[1]);
  return [...new Set(matches)];
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const handleCopyPublicLink = (e, campaign) => {
  if (e) e.stopPropagation();
  const userName = localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'user';
  // Use the campaign's stable ID, not its name - renaming a campaign after sharing a
  // name-based link would otherwise break it permanently.
  const link = `${window.location.origin}/campaign/${encodeURIComponent(userName)}/${campaign.id}`;
  navigator.clipboard.writeText(link);
  alert(`Public campaign link copied to clipboard!\n\n${link}`);
};

const ExpandableTableText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const limit = 80;
  
  if (!text) return '-';
  if (text.length <= limit) return <span>{text}</span>;
  
  return (
    <span>
      {isExpanded ? text : `${text.slice(0, limit)}...`}
      <span 
        style={{ color: '#00a884', fontWeight: '700', cursor: 'pointer', marginLeft: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? ' Show less' : ' Read more'}
      </span>
    </span>
  );
};


const renderParsedBody = (text, variables = {}) => {
  if (!text) return null;
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return parts.map((part, index) => {
    if (part.startsWith('{{') && part.endsWith('}}')) {
      const varNumber = part.replace(/[^0-9]/g, '');
      const liveValue = variables[varNumber];
      return (
        <span key={index} style={{ color: '#00A884', fontWeight: '700' }}>
          {liveValue || part}
        </span>
      );
    }
    return part;
  });
};

const renderWhatsAppPreview = (template, variables = {}) => {
  if (!template) return null;
  
  const headerType = (template.header_type || 'NONE').toUpperCase();
  const autoMediaUrl = 
      template.uploaded_preview_image ||
      template.meta_media_url ||
      template.header_media ||
      (template.sample_values && (template.sample_values.header_handle || template.sample_values.media_url)) ||
      '';
  
  return (
    <div className="whatsapp-preview-container" style={{ 
      padding: '16px', 
      background: 'var(--bg-main)', 
      borderRadius: '16px', 
      border: '1px solid var(--border-color)', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '8px', 
      width: '100%', 
      maxHeight: '360px', 
      overflowY: 'auto', 
      boxSizing: 'border-box' 
    }}>
      <div className="message-bubble" style={{ 
        background: 'var(--surface-white)', 
        border: '1px solid var(--border-color)', 
        borderRadius: '0 16px 16px 16px', 
        padding: '12px 14px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        maxWidth: '280px',
        width: '100%',
        alignSelf: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
        flexShrink: 0
      }}>
        {/* Header Image */}
        {headerType === 'IMAGE' && (
          autoMediaUrl ? (
            <div style={{
              width: '100%',
              height: '110px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '10px',
              border: '1px solid var(--border-color)'
            }}>
              {(autoMediaUrl.startsWith('http') || autoMediaUrl.startsWith('data:') || autoMediaUrl.startsWith('blob:')) ? (
                <img 
                  src={autoMediaUrl} 
                  alt="Template Header" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                <div className="meta-handle-display-box" style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRadius: '12px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', height: '100%', justifyContent: 'center', boxSizing: 'border-box' }}>
                  <span style={{ fontSize: '20px' }}>🖼️</span>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--brand-purple)', textAlign: 'center' }}>Meta Sync Media</span>
                  <span style={{ fontSize: '9px', color: '#64748b', fontFamily: 'monospace', textAlign: 'center', wordBreak: 'break-all' }}>ID: {autoMediaUrl}</span>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              width: '100%',
              height: '110px',
              background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.04) 0%, rgba(99, 102, 241, 0.08) 100%)',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: 'var(--brand-purple)',
              marginBottom: '10px',
              border: '1.5px dashed rgba(117, 81, 255, 0.3)',
              overflow: 'hidden'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8 }}>Header Image Placeholder</span>
            </div>
          )
        )}

        {/* Header Video */}
        {headerType === 'VIDEO' && (
          <div style={{
            width: '100%',
            height: '110px',
            background: '#e2e8f0',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: '#64748b',
            marginBottom: '10px',
            border: '1px solid #cbd5e1'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px', color: '#475569' }}>
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>
            <span style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Header Video</span>
          </div>
        )}

        {/* Header Document */}
        {headerType === 'DOCUMENT' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            background: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: '10px',
            marginBottom: '10px'
          }}>
            <div style={{
              background: '#ef4444',
              padding: '8px',
              borderRadius: '8px',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>document.pdf</span>
              <span style={{ fontSize: '10px', color: '#64748b' }}>PDF Document</span>
            </div>
          </div>
        )}

        {/* Header Text */}
        {headerType === 'TEXT' && template.header_text && (
          <div style={{
            fontSize: '14px',
            fontWeight: '800',
            color: '#0f172a',
            marginBottom: '6px',
            lineHeight: '1.3'
          }}>
            {template.header_text}
          </div>
        )}

        {/* Body Content */}
        <div style={{ fontSize: '13px', color: 'var(--text-dark)', lineHeight: '1.5', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
          {renderParsedBody(template.body || template.body_preview, variables)}
        </div>

        {/* Footer Text */}
        {template.footer_text && (
          <div style={{
            fontSize: '11px',
            color: 'var(--text-gray)',
            marginTop: '6px',
            borderTop: '1px dashed var(--border-color)',
            paddingTop: '6px'
          }}>
            {template.footer_text}
          </div>
        )}
      </div>

      {/* Template Action Buttons */}
      {template.buttons && Array.isArray(template.buttons) && template.buttons.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '280px',
          alignSelf: 'center',
          background: 'var(--surface-white)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          flexShrink: 0
        }}>
          {template.buttons.map((btn, idx) => (
            <div 
              key={idx}
              style={{
                padding: '10px',
                textAlign: 'center',
                fontSize: '12.5px',
                fontWeight: '700',
                color: 'var(--brand-purple)',
                borderBottom: idx < template.buttons.length - 1 ? '1px solid var(--border-color)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                background: 'var(--surface-white)'
              }}
            >
              {btn.type === 'PHONE_NUMBER' && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              )}
              {btn.type === 'URL' && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
              )}
              <span>{btn.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CampaignsView({ mode: initialMode = 'active', setCampaignMode, setActiveView, selectedCampaignId, setSelectedCampaignId }) {
  const navigate = useNavigate();
  const { activeConfigId } = useConfig();
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    if (initialMode && initialMode !== mode) {
      setMode(initialMode);
    }
  }, [initialMode]);


  const copy = PAGE_COPY[mode] ?? PAGE_COPY.active;
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    template_id: null,
    variables: {},
  });
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [sendTarget, setSendTarget] = useState(null);
  const [sendTemplateId, setSendTemplateId] = useState(null);
  const [sendVariables, setSendVariables] = useState({});
  const [initializedSendCampaignId, setInitializedSendCampaignId] = useState(null);
  const [sendTemplateDropdownOpen, setSendTemplateDropdownOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sendTemplateSearch, setSendTemplateSearch] = useState('');
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailMessages, setDetailMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [isDeletingCampaign, setIsDeletingCampaign] = useState(false);

  const loadCampaigns = async () => {
    if (mode === 'history') {
      setCampaigns(toList(await getCampaignHistory()));
      return;
    }

    if (mode === 'scheduled') {
      setCampaigns(toList(await getScheduledCampaigns()));
      return;
    }

    const data = toList(await getCampaigns());
    setCampaigns(data.filter((campaign) => !['STOPPED', 'COMPLETED', 'FAILED'].includes(campaign.status)));
  };

  // Lazy load templates only when required
  const fetchTemplatesOnDemand = useCallback(async () => {
    if (!activeConfigId || templates.length > 0 || loadingTemplates) return;
    try {
      setLoadingTemplates(true);
      const data = await getWhatsAppTemplates();
      setTemplates(toList(data));
    } catch (err) {
      setNotice({ type: 'error', message: getApiErrorMessage(err, 'Failed to load templates') });
    } finally {
      setLoadingTemplates(false);
    }
  }, [activeConfigId, templates.length, loadingTemplates]);

  useEffect(() => {
    if (sendTarget || sendTemplateDropdownOpen) {
      fetchTemplatesOnDemand();
    }
  }, [sendTarget, sendTemplateDropdownOpen, fetchTemplatesOnDemand]);

  useEffect(() => {
    if (!activeConfigId) {
      setLoading(false);
      setCampaigns([]);
      return;
    }
    const loadData = async () => {
      try {
        // Only set loading true if we have no campaigns yet, to avoid jarring spinners on tab switch if possible.
        if (campaigns.length === 0) setLoading(true);
        setNotice(null);

        if (mode === 'active') {
          const campaignsData = await getCampaigns();
          setCampaigns(toList(campaignsData).filter((campaign) => !['STOPPED', 'COMPLETED', 'FAILED'].includes(campaign.status)));
          return;
        }

        await loadCampaigns();
      } catch (error) {
        setNotice({
          type: 'error',
          message: getApiErrorMessage(error, 'Failed to load campaign data'),
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mode, activeConfigId]);

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => {
        setNotice(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  useEffect(() => {
    const loadAllContacts = async () => {
      try {
        const contactsData = await getContacts();
        setContacts(toList(contactsData));
        setSelectedContacts([]);
        setFilterText('');
      } catch (error) {
        setNotice({
          type: 'error',
          message: getApiErrorMessage(error, 'Failed to load contacts'),
        });
      }
    };

    if (showCreateModal) {
      loadAllContacts();
    }
  }, [showCreateModal, activeConfigId]);



  // Sync selectedCampaignId prop with component state
  useEffect(() => {
    if (selectedCampaignId) {
      if (detailCampaign && String(detailCampaign.id) === String(selectedCampaignId)) {
        return;
      }
      
      const existing = campaigns.find(c => String(c.id) === String(selectedCampaignId));
      if (existing) {
        openCampaignDetail(existing);
      } else {
        const fetchAndOpen = async () => {
          setDetailLoading(true);
          try {
            const campaign = await getCampaign(selectedCampaignId);
            setDetailCampaign(campaign);
            setDetailMessages(toList(await getCampaignMessages(selectedCampaignId)));
          } catch (error) {
            setNotice({
              type: 'error',
              message: 'Failed to load campaign details',
            });
            if (setSelectedCampaignId) {
              setSelectedCampaignId(null);
            }
          } finally {
            setDetailLoading(false);
          }
        };
        fetchAndOpen();
      }
    } else {
      if (detailCampaign) {
        setDetailCampaign(null);
        setDetailMessages([]);
      }
    }
  }, [selectedCampaignId, campaigns]);

  useEffect(() => {
    if (sendTarget) {
      if (sendTarget.id !== initializedSendCampaignId) {
        setInitializedSendCampaignId(sendTarget.id);
        
        if (sendTarget.template) {
          const tId = typeof sendTarget.template === 'object' ? sendTarget.template.id : sendTarget.template;
          setSendTemplateId(Number(tId));
          
          // Match and populate variables
          const template = templates.find((t) => t.id === Number(tId));
          const targetVars = sendTarget.variables || {};
          const variables = {};
          
          const templateVariables = template 
            ? (Array.isArray(template.variables) ? template.variables : extractTemplateVariables(template))
            : Object.keys(targetVars);
            
          templateVariables.forEach((variable) => {
            variables[String(variable)] = targetVars[String(variable)] || '';
          });
          setSendVariables(variables);
        }
      }
    } else {
      setInitializedSendCampaignId(null);
    }
  }, [sendTarget, templates, initializedSendCampaignId]);

  const selectedSendTemplate = useMemo(
    () => templates.find((template) => template.id === sendTemplateId),
    [sendTemplateId, templates]
  );

  const sendRequiredVariables = Array.isArray(selectedSendTemplate?.variables)
    ? selectedSendTemplate.variables
    : extractTemplateVariables(selectedSendTemplate);

  const filteredContacts = contacts.filter((contact) => {
    const term = filterText.toLowerCase();
    return (
      (contact.name || '').toLowerCase().includes(term) ||
      (contact.phone_number || '').includes(filterText)
    );
  });

  const handleContactToggle = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
      return;
    }
    setSelectedContacts(filteredContacts.map((contact) => contact.id));
  };

  const handleSendTemplateSelect = (templateId) => {
    const template = templates.find((item) => item.id === templateId);
    const variables = {};
    const templateVariables = Array.isArray(template?.variables)
      ? template.variables
      : extractTemplateVariables(template);

    templateVariables.forEach((variable) => {
      variables[String(variable)] = '';
    });

    setSendTemplateId(templateId);
    setSendVariables(variables);
  };

  const resetCreateForm = () => {
    setCampaignForm({ name: '', course_id: null, template_id: null, variables: {} });
    setSelectedContacts([]);
    setFilterText('');
    setShowCreateModal(false);
  };

  const resetSendForm = () => {
    setSendTarget(null);
    setSendTemplateId(null);
    setSendVariables({});
    setSendTemplateDropdownOpen(false);
    setSendTemplateSearch('');
  };

  const handleCreateCampaign = async (event) => {
    event.preventDefault();

    if (!campaignForm.name.trim()) {
      setNotice({ type: 'error', message: 'Campaign name is required' });
      return;
    }

    if (selectedContacts.length === 0) {
      setNotice({ type: 'error', message: 'Please select at least one contact' });
      return;
    }

    try {
      setSaving(true);
      const newCampaign = await createCampaign({
        name: campaignForm.name.trim(),
        contact_ids: selectedContacts,
      });
      setCampaigns((prev) => [newCampaign, ...prev]);
      setNotice({ type: 'success', message: 'Campaign created successfully.' });
      resetCreateForm();
      await loadCampaigns();
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to create campaign'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendCampaign = async (event) => {
    event.preventDefault();

    if (!sendTarget) return;

    if (!sendTemplateId) {
      setNotice({ type: 'error', message: 'Please select a template to send' });
      return;
    }

    const missingVariable = sendRequiredVariables.find((variable) => !sendVariables[String(variable)]?.trim());
    if (missingVariable) {
      setNotice({ type: 'error', message: `Please enter a value for variable {{${missingVariable}}}` });
      return;
    }

    try {
      setSaving(true);
      const result = await launchCampaign(sendTarget.id, {
        template_id: sendTemplateId,
        variables: sendVariables,
      });
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === sendTarget.id
            ? {
              ...campaign,
              status: result.status || 'RUNNING',
              total_sent: result.total_sent ?? campaign.total_sent,
              total_failed: result.total_failed ?? campaign.total_failed,
            }
            : campaign
        ).filter((campaign) => !['STOPPED', 'COMPLETED', 'FAILED'].includes(campaign.status))
      );
      setDetailCampaign((current) =>
        current?.id === sendTarget.id
          ? {
            ...current,
            status: result.status || 'RUNNING',
            total_sent: result.total_sent ?? current.total_sent,
            total_failed: result.total_failed ?? current.total_failed,
          }
          : current
      );
      if (detailCampaign?.id === sendTarget.id) {
        setDetailMessages(toList(await getCampaignMessages(sendTarget.id)));
      }
      setNotice({ type: 'success', message: 'Campaign sent successfully.' });
      resetSendForm();
      await loadCampaigns();
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to send campaign'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleCampaign = async (event) => {
    event.preventDefault();
    if (!scheduleTarget || !scheduleAt) {
      setNotice({ type: 'error', message: 'Please choose a schedule date and time' });
      return;
    }

    try {
      setSaving(true);
      const scheduledAtIso = new Date(scheduleAt).toISOString();
      await scheduleCampaign(scheduleTarget.id, scheduledAtIso);
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === scheduleTarget.id
            ? { ...campaign, status: 'SCHEDULED', scheduled_at: scheduledAtIso }
            : campaign
        )
      );
      setNotice({ type: 'success', message: 'Campaign scheduled successfully.' });
      setScheduleTarget(null);
      setScheduleAt('');
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to schedule campaign'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
  };

  const handleConfirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    try {
      setIsDeletingCampaign(true);
      await deleteCampaign(campaignToDelete.id);
      setCampaigns((prev) => prev.filter((campaign) => campaign.id !== campaignToDelete.id));
      setNotice({ type: 'delete', message: 'Campaign deleted successfully.' });
      await loadCampaigns();
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to delete campaign'),
      });
    } finally {
      setIsDeletingCampaign(false);
      setCampaignToDelete(null);
    }
  };

  const handleStopCampaign = async (campaignId) => {
    if (!window.confirm('Stop this campaign and move it to history?')) return;

    try {
      setSaving(true);
      await stopCampaign(campaignId);
      setCampaigns((prev) => prev.filter((campaign) => campaign.id !== campaignId));
      setDetailCampaign((current) => (current?.id === campaignId ? null : current));
      setDetailMessages((current) => (detailCampaign?.id === campaignId ? [] : current));
      setNotice({ type: 'success', message: 'Campaign stopped and moved to history.' });
      await loadCampaigns();
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to stop campaign'),
      });
    } finally {
      setSaving(false);
    }
  };

  const openCampaignDetail = async (campaign) => {
    setDetailCampaign(campaign);
    setDetailMessages([]);
    setDetailLoading(true);
    if (setSelectedCampaignId && String(selectedCampaignId) !== String(campaign.id)) {
      setSelectedCampaignId(campaign.id);
    }

    try {
      setDetailMessages(toList(await getCampaignMessages(campaign.id)));
    } catch (error) {
      setNotice({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to load sent campaign messages'),
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeCampaignDetail = () => {
    setDetailCampaign(null);
    setDetailMessages([]);
    if (setSelectedCampaignId && selectedCampaignId !== null) {
      setSelectedCampaignId(null);
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (setCampaignMode) {
      setCampaignMode(newMode);
    }
    closeCampaignDetail();
  };

  if (loading) {
    return <div className="campaigns-page">Loading campaigns...</div>;
  }

  return (
    <div className="campaigns-page">
      {/* ── Desktop header: tabs + create button ── */}
      <div className="campaigns-header-desktop" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px', width: '100%', boxSizing: 'border-box' }}>
        <div 
          className="campaign-tabs-container" 
          style={{ 
            display: 'flex', 
            gap: '4px', 
            background: 'var(--border-color)', 
            padding: '4px', 
            borderRadius: '99px', 
            width: 'fit-content',
            maxWidth: '100%',
            overflow: 'hidden',
            border: '1px solid var(--border-color)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
        >
          {[
            { id: 'active', label: 'Campaigns', shortLabel: 'Active' },
            { id: 'history', label: 'Campaign History', shortLabel: 'History' },
            { id: 'scheduled', label: 'Campaign Scheduled', shortLabel: 'Scheduled' }
          ].map((tab) => {
            const isActive = mode === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleModeChange(tab.id)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '99px',
                  border: isActive ? '1px solid var(--border-color)' : '1px solid transparent',
                  cursor: 'pointer',
                  fontSize: '11.5px',
                  fontWeight: '700',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: isActive ? 'var(--surface-white)' : 'transparent',
                  color: 'var(--toggle-text)',
                  boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.06)' : 'none',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--brand-purple)';
                    e.currentTarget.style.background = 'var(--brand-purple-light)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--toggle-text)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span className="tab-label-full">{tab.label}</span>
                <span className="tab-label-short" style={{ display: 'none' }}>{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (setActiveView) setActiveView('analytics');
              else navigate('/whatsapp/analytics?tab=campaign_events');
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '99px',
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'var(--surface-white)',
              color: 'var(--brand-purple)',
              border: '1.5px solid var(--brand-purple)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            Campaign Analytics
          </button>
          <button 
            className="campaign-create-btn" 
            onClick={() => {
              localStorage.removeItem('editCampaignDraftId');
              if (setActiveView) setActiveView('create-campaign');
              else navigate('/whatsapp/campaigns/create');
            }}
            style={{ 
              padding: '8px 18px', 
              borderRadius: '99px', 
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              margin: 0,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
            }}
          >
            <span style={{ fontSize: '14px', lineHeight: 1 }}>+</span> Create Campaign
          </button>
        </div>
      </div>

      {/* ── Mobile header: dropdown + create button in one row ── */}
      <div className="campaigns-header-mobile">
        <div className="camp-mobile-select-wrap" style={{ position: 'relative', flex: 1 }}>
          <div
            className="camp-mobile-select-trigger"
            onClick={() => setMobileDropdownOpen((p) => !p)}
          >
            <span className="camp-mobile-select-label">
              {mode === 'active' ? 'Campaigns' : mode === 'history' ? 'Campaign History' : 'Scheduled'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: mobileDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease', flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
          {mobileDropdownOpen && (
            <div className="camp-mobile-select-menu">
              {[
                { id: 'active', label: 'Campaigns' },
                { id: 'history', label: 'Campaign History' },
                { id: 'scheduled', label: 'Scheduled' }
              ].map((opt) => (
                <div
                  key={opt.id}
                  className={`camp-mobile-select-option${mode === opt.id ? ' active' : ''}`}
                  onClick={() => { handleModeChange(opt.id); setMobileDropdownOpen(false); }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <button 
          className="campaign-create-btn camp-mobile-create-btn" 
          onClick={() => {
            localStorage.removeItem('editCampaignDraftId');
            if (setActiveView) setActiveView('create-campaign');
            else navigate('/whatsapp/campaigns/create');
          }}
        >
          <span style={{ fontSize: '14px', lineHeight: 1 }}>+</span> Create Campaign
        </button>
      </div>

      {notice && (
        <div className={`campaign-notice ${notice.type}`}>
          <span>{notice.message}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">x</button>
        </div>
      )}

      {scheduleTarget && createPortal(
        <div className="wa-module">
          <div className="campaign-modal-overlay" onClick={() => setScheduleTarget(null)}>
            <div className="campaign-modal schedule-modal" onClick={(event) => event.stopPropagation()}>
              <h3>Schedule Campaign</h3>
              <form onSubmit={handleScheduleCampaign} className="campaign-form">
                <p className="schedule-campaign-name">{scheduleTarget.name}</p>
                <label>
                  Send At
                  <input
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(event) => setScheduleAt(event.target.value)}
                    required
                  />
                </label>
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <button type="submit" className="campaign-submit-btn" disabled={saving} style={{ flex: 1 }}>
                    {saving ? 'Scheduling...' : 'Schedule Campaign'}
                  </button>
                  <button type="button" className="campaign-cancel-btn" onClick={() => setScheduleTarget(null)} style={{ flex: 1, margin: 0 }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {sendTarget && createPortal(
        <div className="wa-module">
          <div className="campaign-modal-overlay" onClick={resetSendForm}>
            <div className="campaign-modal" style={{ maxWidth: '480px', width: '100%', margin: '0 16px' }} onClick={(event) => event.stopPropagation()}>
            <h3>Send Campaign</h3>
            <form onSubmit={handleSendCampaign} className="campaign-form">
              <p className="schedule-campaign-name">{sendTarget.name}</p>

              {sendTarget.template ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', width: '100%' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Template
                  </span>
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: 'var(--bg-main, #f8fafc)',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '700',
                      color: 'var(--text-dark)',
                      cursor: 'default',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginRight: '8px', color: 'var(--brand-purple)' }}
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <span>
                      {sendTarget.template_name || (selectedSendTemplate ? (selectedSendTemplate.template_name || selectedSendTemplate.name) : 'WhatsApp Template')}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', width: '100%' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Select Template
                  </span>
                  
                  <div className="custom-dropdown-container" style={{ position: 'relative', width: '100%' }}>
                    <div 
                      className="custom-dropdown-trigger"
                      onClick={() => setSendTemplateDropdownOpen(!sendTemplateDropdownOpen)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'var(--surface-white)',
                        border: sendTemplateDropdownOpen ? '2px solid var(--brand-purple)' : '1.5px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: selectedSendTemplate ? 'var(--text-dark)' : 'var(--text-gray)',
                        cursor: 'pointer',
                        boxShadow: 'var(--shadow-sm)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <span>
                        {selectedSendTemplate ? (selectedSendTemplate.template_name || selectedSendTemplate.name) : 'Select a Template'}
                      </span>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{
                          transform: sendTemplateDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                          transition: 'transform 0.2s ease',
                          color: 'var(--text-gray)'
                        }}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>

                    {sendTemplateDropdownOpen && (
                      <>
                        <div 
                          className="custom-dropdown-list"
                          style={{
                            marginTop: '8px',
                            background: 'var(--surface-white)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            borderRadius: '16px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Search Bar */}
                          <div style={{ padding: '12px', borderBottom: '1px solid rgba(0,0,0,0.04)', background: 'var(--surface-white)' }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-gray)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px' }}>
                                  <circle cx="11" cy="11" r="8"></circle>
                                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                              </svg>
                              <input
                                type="text"
                                placeholder="Search templates..."
                                value={sendTemplateSearch}
                                onChange={(e) => setSendTemplateSearch(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '10px 14px 10px 36px',
                                  border: '1px solid transparent',
                                  borderRadius: '10px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  background: 'var(--bg-main)',
                                  color: 'var(--text-dark)',
                                  outline: 'none',
                                  transition: 'all 0.2s'
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => { e.target.style.border = '1px solid var(--brand-purple)'; e.target.style.background = 'var(--surface-white)'; }}
                                onBlur={(e) => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'var(--bg-main)'; }}
                              />
                            </div>
                          </div>

                          {/* Options List */}
                          <div style={{ overflowY: 'auto', padding: '6px', maxHeight: '200px' }}>
                            {loadingTemplates ? (
                              <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: '13px', color: 'var(--text-gray)' }}>
                                <div style={{ 
                                  width: '20px', height: '20px', 
                                  margin: '0 auto 10px', 
                                  border: '2px solid rgba(0,0,0,0.1)', 
                                  borderTopColor: 'var(--brand-purple)', 
                                  borderRadius: '50%', 
                                  animation: 'spin 1s linear infinite' 
                                }} />
                                Loading templates...
                              </div>
                            ) : templates.filter(t => (t.template_name || t.name || '').toLowerCase().includes(sendTemplateSearch.toLowerCase())).length === 0 ? (
                              <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: 'var(--text-gray)' }}>
                                No templates found
                              </div>
                            ) : (
                              templates
                                .filter(t => (t.template_name || t.name || '').toLowerCase().includes(sendTemplateSearch.toLowerCase()))
                                .map((template) => {
                                  const isApproved = (template.status || '').toUpperCase() === 'APPROVED';
                                  const isRejected = (template.status || '').toUpperCase() === 'REJECTED';
                                  
                                  let statusText = 'PENDING';
                                  let statusColor = '#f59e0b'; // amber
                                  let statusBg = 'rgba(245, 158, 11, 0.1)';
                                  
                                  if (isApproved) {
                                    statusText = 'APPROVED';
                                    statusColor = '#10b981'; // green
                                    statusBg = 'rgba(16, 185, 129, 0.1)';
                                  } else if (isRejected) {
                                    statusText = 'REJECTED';
                                    statusColor = '#ef4444'; // red
                                    statusBg = 'rgba(239, 68, 68, 0.1)';
                                  }

                                  return (
                                    <div
                                      key={template.id}
                                      onClick={() => {
                                        if (isApproved) {
                                          handleSendTemplateSelect(template.id);
                                          setSendTemplateDropdownOpen(false);
                                          setSendTemplateSearch('');
                                        }
                                      }}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        cursor: isApproved ? 'pointer' : 'not-allowed',
                                        background: 'transparent',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        opacity: isApproved ? 1 : 0.6,
                                        marginBottom: '4px'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (isApproved) {
                                          e.currentTarget.style.background = 'var(--brand-purple-light, rgba(124, 77, 255, 0.08))';
                                          e.currentTarget.style.transform = 'translateX(4px)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {!isApproved && (
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--text-gray)' }}>
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                          </svg>
                                        )}
                                        <span style={{ fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)' }}>
                                          {template.template_name || template.name}
                                        </span>
                                      </div>

                                      <span style={{
                                          fontSize: '11px',
                                          fontWeight: '800',
                                          color: statusColor,
                                          background: statusBg,
                                          padding: '4px 10px',
                                          borderRadius: '20px',
                                          letterSpacing: '0.5px'
                                        }}>
                                          {statusText}
                                      </span>
                                    </div>
                                  );
                                })
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {selectedSendTemplate && (
                <div style={{ marginBottom: '20px', width: '100%' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-gray)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>
                    Live Preview
                  </span>
                  {renderWhatsAppPreview(selectedSendTemplate, sendVariables)}
                </div>
              )}

              {sendRequiredVariables.length > 0 && (
                <div className="campaign-variables-section">
                  <h4>Template Variables</h4>
                  {sendRequiredVariables.map((variable) => (
                    <label key={variable}>
                      {`{{${variable}}}`}
                      <input
                        type="text"
                        value={sendVariables[String(variable)] || ''}
                        onChange={(event) =>
                          setSendVariables((prev) => ({
                            ...prev,
                            [String(variable)]: event.target.value,
                          }))
                        }
                        placeholder={`Value for {{${variable}}}`}
                      />
                    </label>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button type="submit" className="campaign-submit-btn" disabled={saving} style={{ flex: 1 }}>
                  {saving ? 'Sending...' : 'Send Now'}
                </button>
                <button type="button" className="campaign-cancel-btn" onClick={resetSendForm} style={{ flex: 1, margin: 0 }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>,
        document.body
      )}

      <DeleteConfirmModal
        isOpen={Boolean(campaignToDelete)}
        itemName={campaignToDelete?.name}
        title="Delete Campaign?"
        message="will be permanently deleted. This action cannot be undone."
        confirmText="Delete Campaign"
        onConfirm={handleConfirmDeleteCampaign}
        onCancel={() => setCampaignToDelete(null)}
        isLoading={isDeletingCampaign}
      />

      {detailCampaign ? (
        <div className="campaigns-list campaign-detail-view">
          <div className="campaign-detail-header">
            <div className="campaign-detail-title-wrap">
              <button type="button" className="campaign-back-btn" onClick={closeCampaignDetail}>
                Back
              </button>
              <h3>{detailCampaign.name}</h3>
            </div>

            <div className="campaign-info-stats">
              <span>
                <strong>Status:</strong>{' '}
                <span className={`status-${String(detailCampaign.status || '').toLowerCase()}`}>
                  {detailCampaign.status}
                </span>
              </span>
              <span><strong>Recipients:</strong> {detailCampaign.total_contacts ?? 0}</span>
              <span><strong>Sent / Failed:</strong> {detailCampaign.total_sent ?? 0} / {detailCampaign.total_failed ?? 0}</span>
              {detailCampaign.scheduled_at && <span><strong>Scheduled:</strong> {formatDateTime(detailCampaign.scheduled_at)}</span>}
            </div>

            <div className="campaign-detail-actions" style={{ display: 'flex', gap: '8px' }}>

              {mode === 'active' && (
                <>
                  <button className="action-btn send-btn" onClick={() => setSendTarget(detailCampaign)} disabled={saving}>
                    Send
                  </button>
                  <button className="action-btn stop-btn" onClick={() => handleStopCampaign(detailCampaign.id)} disabled={saving}>
                    Stop
                  </button>
                </>
              )}
            </div>
          </div>

          {detailLoading ? (
            <p className="no-campaigns">Loading campaign details...</p>
          ) : detailMessages.length === 0 ? (
            <p className="no-campaigns">No messages sent in this campaign.</p>
          ) : (
            <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
              <table className="campaigns-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {detailMessages.map((message) => (
                    <tr key={message.id}>
                      <td data-label="Contact">{message.contact_name || '-'}</td>
                      <td data-label="Phone">{message.contact_phone || '-'}</td>
                      <td className="campaign-message-cell" data-label="Message"><ExpandableTableText text={message.text} /></td>
                      <td data-label="Status">
                        <span className={`status-${String(message.status || '').toLowerCase()}`}>
                          {message.status}
                        </span>
                      </td>
                      <td data-label="Sent At">
                        {(() => {
                          const status = (message.status || '').toUpperCase();
                          let ts = message.timestamp || message.created_at;
                          if (status === 'READ' && message.read_at) ts = message.read_at;
                          else if (status === 'DELIVERED' && message.delivered_at) ts = message.delivered_at;
                          else if (status === 'SENT' && message.sent_at) ts = message.sent_at;
                          else if (status === 'FAILED' && message.failed_at) ts = message.failed_at;
                          return formatDateTime(ts);
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="campaigns-list">
          {campaigns.length === 0 ? (
            <p className="no-campaigns">{copy.empty}</p>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', width: '100%' }}>
              <table className="campaigns-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center' }}>Campaign Name</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Recipients</th>
                    {mode !== 'scheduled' && <th style={{ textAlign: 'center' }}>Sent / Failed</th>}
                    {mode === 'scheduled' && <th style={{ textAlign: 'center' }}>Scheduled At</th>}
                    {mode === 'history' && <th style={{ textAlign: 'center' }}>Completed At</th>}
                    {mode !== 'history' && <th style={{ textAlign: 'center' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr 
                      key={campaign.id} 
                      onClick={() => openCampaignDetail(campaign)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td data-label="Campaign Name" className="camp-name-cell">
                        <span className="campaign-name-button">
                          {campaign.name}
                        </span>
                        {/* Running badge shown inline on mobile only */}
                        <span className={`camp-mobile-status-badge status-${String(campaign.status || '').toLowerCase()}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td data-label="Status" className="camp-status-cell">
                        <span className={`status-${String(campaign.status || '').toLowerCase()}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td data-label="Recipients" className="camp-recipients-cell">{campaign.total_contacts ?? 0}</td>
                      {mode !== 'scheduled' && (
                        <td data-label="Sent / Failed" className="camp-sentfailed-cell">{campaign.total_sent ?? 0} / {campaign.total_failed ?? 0}</td>
                      )}
                      {mode === 'scheduled' && <td data-label="Scheduled At">{formatDateTime(campaign.scheduled_at)}</td>}
                      {mode === 'history' && <td data-label="Completed At">{formatDateTime(campaign.completed_at)}</td>}
                      {mode !== 'history' && (
                        <td className="actions" data-label="Actions" style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', border: 'none', padding: '6px 12px' }}>

                          {/* Send button — hidden for DRAFT */}
                          {mode === 'active' && campaign.status !== 'DRAFT' && ['SCHEDULED', 'RUNNING'].includes(campaign.status) && (
                            <button 
                              className="action-btn send-btn-premium" 
                              style={{ 
                                display: 'flex', alignItems: 'center', gap: '4px', 
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                                color: '#fff', border: 'none', padding: '5px 10px', 
                                borderRadius: '8px', cursor: 'pointer', fontWeight: '700', 
                                fontSize: '11px', boxShadow: '0 2px 6px rgba(16,185,129,0.15)',
                                transition: 'all 0.2s', height: '28px'
                              }} 
                              onClick={(e) => { e.stopPropagation(); setSendTarget(campaign); }} 
                              disabled={saving}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                              Send
                            </button>
                          )}
                          {/* Edit button — for DRAFT, RUNNING, SCHEDULED */}
                          {mode === 'active' && ['DRAFT', 'RUNNING', 'SCHEDULED'].includes(campaign.status) && (
                            <button 
                              className="action-btn edit-btn-premium" 
                              style={{ 
                                display: 'flex', alignItems: 'center', gap: '4px', 
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                                color: '#fff', border: 'none', padding: '5px 10px', 
                                borderRadius: '8px', cursor: 'pointer', fontWeight: '700', 
                                fontSize: '11px', boxShadow: '0 2px 6px rgba(245,158,11,0.15)',
                                transition: 'all 0.2s', height: '28px'
                              }} 
                              onClick={(e) => { 
                                e.stopPropagation();
                                localStorage.setItem('editCampaignDraftId', campaign.id);
                                if (setActiveView) setActiveView('create-campaign');
                                else navigate('/whatsapp/campaigns/create');
                              }} 
                              disabled={saving}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                              Edit
                            </button>
                          )}
                          {/* Stop button — hidden for DRAFT */}
                          {mode === 'active' && campaign.status !== 'DRAFT' && !['STOPPED', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
                            <button 
                              className="action-btn stop-btn-premium" 
                              style={{ 
                                display: 'flex', alignItems: 'center', gap: '4px', 
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
                                color: '#fff', border: 'none', padding: '5px 10px', 
                                borderRadius: '8px', cursor: 'pointer', fontWeight: '700', 
                                fontSize: '11px', boxShadow: '0 2px 6px rgba(239,68,68,0.15)',
                                transition: 'all 0.2s', height: '28px'
                              }} 
                              onClick={(e) => { e.stopPropagation(); handleStopCampaign(campaign.id); }} 
                              disabled={saving}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect></svg>
                              Stop
                            </button>
                          )}
                          {/* Messages button */}
                          <button
                            type="button"
                            className="action-btn"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              background: 'var(--surface-soft)', border: '1px solid var(--border-color)',
                              color: 'var(--text-dark)', padding: '5px 10px',
                              borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
                              fontSize: '11px', transition: 'all 0.2s', height: '28px'
                            }}
                            onClick={(e) => { e.stopPropagation(); openCampaignDetail(campaign.id); }}
                            title="View sent campaign messages"
                          >
                            Messages
                          </button>

                          {/* Analytics button (Redirects to Campaign Event Analytics) */}
                          <button
                            type="button"
                            className="action-btn"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                              color: '#fff', border: 'none', padding: '5px 12px',
                              borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
                              fontSize: '11px', boxShadow: '0 2px 6px rgba(6,182,212,0.2)',
                              transition: 'all 0.2s', height: '28px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/whatsapp/analytics?tab=campaign_events&campaign=${campaign.id}`);
                            }}
                            title={`View analytics for ${campaign.name}`}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="20" x2="18" y2="10" />
                              <line x1="12" y1="20" x2="12" y2="4" />
                              <line x1="6" y1="20" x2="6" y2="14" />
                            </svg>
                            Analytics
                          </button>

                          {/* Public Link button */}
                          <button
                            type="button"
                            className="action-btn"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              background: 'var(--surface-soft)', border: '1px solid var(--border-color)',
                              color: '#6366f1', padding: '5px 10px',
                              borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
                              fontSize: '11px', transition: 'all 0.2s', height: '28px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const userName = localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'user';
                              const publicUrl = `${window.location.origin}/campaign/${encodeURIComponent(userName)}/${campaign.id}`;
                              navigator.clipboard.writeText(publicUrl);
                              setNotice({ type: 'success', message: 'Campaign public link copied to clipboard!' });
                            }}
                            title="Copy candidate registration URL"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                            Link
                          </button>

                          <button 
                            className="action-btn delete-btn-premium" 
                            style={{ 
                              display: 'flex', alignItems: 'center', gap: '4px', 
                              background: 'var(--surface-soft)', border: '1px solid var(--border-color)',
                              color: 'var(--text-gray)', padding: '5px 10px', 
                              borderRadius: '8px', cursor: 'pointer', fontWeight: '700', 
                              fontSize: '11px', transition: 'all 0.2s', height: '28px'
                            }} 
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fee2e2';
                              e.currentTarget.style.color = '#ef4444';
                              e.currentTarget.style.borderColor = '#fca5a5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f8fafc';
                              e.currentTarget.style.color = '#64748b';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                            }}
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(campaign); }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



